import Fuse from 'fuse.js';
import * as React from 'react';

type Search<T> = {
  search: (query: string, options?: {limit: number}) => T[];
};

type Options<T> = {
  // Orders results with equal fuzzy-match scores (e.g. "2023 Vegas trip" vs
  // "2024 Vegas trip" for the query "Vegas").
  tieBreak?: (a: T, b: T) => number;
};

export const useSearch = <T>(
  allItems: T[],
  searchKeys: string[],
  options?: Options<T>,
): Search<T> => {
  const tieBreak = options?.tieBreak;

  // Every call site writes its keys inline (`['name']`), so the array is a new
  // reference on every render. Joined to a string for the memo below, which
  // would otherwise never hit and would be no better than rebuilding.
  const keyId = searchKeys.join(' ');

  // Memoised rather than built inline. This was `useRef(makeFuse())`, which
  // evaluates its argument on every render and keeps only the first result, so
  // a page mounting one picker per row rebuilt an index per row per render and
  // discarded all of them. The payee logo settings page is exactly that: a
  // hundred LogoSelects over the same 200-odd logos, reindexed on every
  // keystroke in the page's search box. Indexing costs time proportional to
  // the list, and none of that work was ever used.
  const fuse = React.useMemo(
    () =>
      new Fuse(allItems, {
        keys: searchKeys,
        threshold: 0.4,
        includeScore: true,
      }),
    // searchKeys is deliberately absent: it is a fresh array every render and
    // is compared by content through keyId instead.
    [allItems, keyId],
  );

  return {
    search: (query: string, searchOptions?: {limit: number}) => {
      if (!query) {
        return allItems;
      }
      const results = fuse.search(query, searchOptions);
      if (tieBreak) {
        results.sort(
          (a, b) => (a.score ?? 0) - (b.score ?? 0) || tieBreak(a.item, b.item),
        );
      }
      return results.map((result) => result.item);
    },
  };
};
